import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";

import { FieldTypeIcon } from "@/shared/components/filters/fieldTypeIcons";
import type { FilterDefinition } from "@/shared/components/filters/FilterDefinition";
import {
  effectiveComparators,
  effectivePresets,
  type Filter,
  filterSchema,
  getDefaultsForVariant,
  isValueless,
} from "@/shared/components/filters/FilterSchema";
import { FilterValueInput } from "@/shared/components/filters/FilterValueInput";
import { Button } from "@/shared/foundation/primitives/Button";

import styles from "./FilterForm.module.css";
import { OperatorPicker } from "./OperatorPicker";

interface FilterFormProps {
  /** The field the user picked in the left pane. */
  selectedField: FilterDefinition;

  /** Fired when the user clicks Add filter (or Cmd/Ctrl+Enter on a non-text input). */
  onCommit: (filter: Filter) => void;

  /** Fired when the user clicks Cancel. */
  onCancel: () => void;

  /**
   * Seeds the form with an existing filter's comparator and values (the
   * chip-edit flow). Must match `selectedField`'s type/field. When absent,
   * the form starts from the field's blank defaults (the add flow).
   */
  initialFilter?: Filter;

  /** Submit button label. Defaults to `"Add filter"`; the edit popover
   *  passes `"Update filter"`. */
  submitLabel?: string;
}

/**
 * The right pane of the filter menu — and the entire form.
 */
export function FilterForm({
  selectedField,
  onCommit,
  onCancel,
  initialFilter,
  submitLabel = "Add filter",
}: FilterFormProps) {
  const form = useForm<Filter>({
    resolver: zodResolver(filterSchema),
    defaultValues: initialFilter ?? getDefaultsForVariant(selectedField),
    // Continuous validation so the Add filter button reflects validity
    // live as the user fills the value input(s).
    mode: "onChange",
  });

  const filter = form.watch();

  const setValues = (next: Filter["values"]) =>
    form.setValue("values", next, {
      shouldValidate: true,
      shouldDirty: true,
    });

  return (
    <form
      // No filter fields need browser provided autocomplete
      autoComplete="off"
      className={styles.panel}
      onSubmit={(event) => {
        void form.handleSubmit((values) => onCommit(values))(event);
      }}
    >
      <section className={styles.content}>
        <FilterFormHeader field={selectedField} />
        <section className={styles.section} aria-label="Operator">
          <h4 className={styles.sectionLabel}>Operator</h4>
          <Controller
            name="comparator"
            control={form.control}
            render={({ field }) => (
              <OperatorPicker
                fieldType={selectedField.type}
                value={field.value}
                onChange={(next) => {
                  field.onChange(next);
                  if (selectedField.type === "identity") {
                    // An identity operator IS the principal type, so every
                    // operator change moves to a different option space —
                    // `user:alice` means nothing under IS_DEVICE. Drop the
                    // selection, labels and all.
                    setValues([]);
                  } else if (isValueless(next)) {
                    // Clear the value if the user selects a valueless operator
                    setValues([]);
                  }
                }}
                operators={effectiveComparators(selectedField)}
                presets={effectivePresets(selectedField)}
              />
            )}
          />
        </section>
        {!isValueless(filter.comparator) && (
          <section className={styles.section} aria-label="Value">
            <h4 className={styles.sectionLabel}>Value</h4>
            <FilterValueInput
              filter={filter}
              definition={selectedField}
              onChange={setValues}
            />
          </section>
        )}
      </section>
      <div className={styles.footer}>
        <Button
          type="button"
          variant="text"
          size="small"
          onClick={onCancel}
          className={styles.footerAction}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="small"
          disabled={!form.formState.isValid}
          className={styles.footerAction}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

function FilterFormHeader({ field }: { field: FilterDefinition }) {
  return (
    <header className={styles.header}>
      <div className={styles.headerGroup}>
        {field.icon ? (
          <span className={styles.headerIcon}>{field.icon}</span>
        ) : (
          <FieldTypeIcon type={field.type} className={styles.headerIcon} />
        )}
        <h3 className={styles.headerName}>{field.name}</h3>
      </div>
      <span className={styles.headerType}>{field.type}</span>
    </header>
  );
}
